import { isNotNullish } from '@chainflip/utils/guard';
import classNames from 'classnames';
import Button from '@/shared/components/flip-ui-kit/Button';
import MinusIcon from '@/shared/icons/flip-ui-kit/small/Minus';
import PlusIcon from '@/shared/icons/flip-ui-kit/small/Plus';
import { POSITIVE_DECIMALS_REGEX } from '@/shared/utils/regex';

export default function StrategyPriceInput({
  label,
  className,
  value,
  error,
  onChange,
  disabled,
}: {
  label: React.ReactNode;
  className?: string;
  value?: string;
  error?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const minPrice = 0.99;
  const maxPrice = 1.01;
  const decimals = 4;

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let sanitizedValue = e.target.value.replace(',', '.');
    sanitizedValue = sanitizedValue.replace(/^\.$/g, '0.'); // replace "." with "0."
    sanitizedValue = sanitizedValue.replace(/^00+/g, '0'); // replace multiple leading 0

    if (POSITIVE_DECIMALS_REGEX.test(sanitizedValue)) {
      onChange(sanitizedValue);
    }
  };
  const onInputBlur = () => {
    if (value) {
      onChange((Math.round(Number(value) * 10 ** decimals) / 10 ** decimals).toString());
    }
  };

  const onPlusClick = () => {
    if (value) {
      onChange(
        (
          Math.round((Number(value) + 10 ** -decimals) * 10 ** decimals) /
          10 ** decimals
        ).toString(),
      );
    }
  };

  const onMinusClick = () => {
    if (value) {
      onChange(
        (
          Math.round((Number(value) - 10 ** -decimals) * 10 ** decimals) /
          10 ** decimals
        ).toString(),
      );
    }
  };

  return (
    <div>
      <div
        className={classNames(
          'flex grow items-center justify-between rounded-md border border-cf-gray-4 bg-cf-gray-3-5 px-4 py-2',
          className,
          isNotNullish(error) && 'border-cf-red-1',
          disabled && 'cursor-not-allowed [&>*]:pointer-events-none',
        )}
      >
        <div className="flex flex-col">
          <span className="text-12 text-cf-light-2">{label}</span>

          <input
            placeholder="0"
            type="text"
            inputMode="decimal"
            className="w-[150px] bg-cf-gray-3-5 text-20 text-white outline-none placeholder:text-cf-light-2"
            value={value}
            onChange={onInputChange}
            onBlur={onInputBlur}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                onMinusClick();
              } else if (e.key === 'ArrowUp') {
                onPlusClick();
              }
            }}
            disabled={disabled}
          />
        </div>
        <div className="flex flex-col space-y-4 text-14 font-medium text-cf-light-2">
          <Button
            type="secondary-standard"
            size="small"
            className="!h-[24px] !w-[24px] !min-w-[24px] !p-0 disabled:opacity-40"
            disabled={disabled || value === '' || Number(value) >= maxPrice}
            onClick={onPlusClick}
            tabbable={false}
          >
            <PlusIcon className="text-cf-light-2" />
          </Button>
          <Button
            type="secondary-standard"
            size="small"
            className="!h-[24px] !w-[24px] !min-w-[24px] !p-0 disabled:opacity-40"
            disabled={disabled || value === '' || Number(value) <= minPrice}
            onClick={onMinusClick}
            tabbable={false}
          >
            <MinusIcon className="text-cf-light-2" />
          </Button>
        </div>
      </div>
      {error && <span className="text-12 text-cf-red-1">{error}</span>}
    </div>
  );
}
